import { NO, UINativeScrollView, UITextView, UIView } from "uicore-ts"
import { RHColors, RHDialogs, RHFont, RHRadius, RHSpacing } from "./Utils_slop/RHDesignTokens"
import { RHValidationIssuesDialogViewTokens } from "./Utils_slop/RHViewTokens"


export type RHValidationIssue = {
    focusTarget: UIView
    message: string
}


export class RHValidationIssuesDialogView extends UIView {

    readonly titleLabel = new UITextView(undefined, UITextView.type.header4)
        .configuredWithObject({
            text: "Palun paranda järgmised väljad",
            textColor: RHColors.textPrimary,
            textAlignment: UITextView.textAlignment.center,
            isSingleLine: NO,
            style: {
                fontWeight: RHDialogs.confirmationAlertTextFontWeight
            }
        })
        .addedAsSubviewToView(this)

    readonly guidanceLabel = new UITextView(undefined, UITextView.type.paragraph)
        .configuredWithObject({
            text: "Enne jätkamist kontrolli kõiki allolevaid põhjuseid. " +
                "Vigased sisestusväljad on vormil punasega märgitud.",
            textColor: RHColors.textMuted,
            isSingleLine: NO,
            style: {
                fontSize: RHFont.sizeMedium,
                lineHeight: RHFont.lineHeightBase
            }
        })
        .addedAsSubviewToView(this)

    readonly issuesScrollView = new UINativeScrollView()
        .configuredWithObject({
            backgroundColor: RHColors.dangerPinkSurface.colorWithAlpha(0.35)
        })
        .addedAsSubviewToView(this)

    readonly _issueLabels: UITextView[]

    constructor(validationIssues: RHValidationIssue[]) {
        super()

        this.issuesScrollView.scrollsX = NO
        this.issuesScrollView.setBorder(
            RHRadius.medium,
            1,
            RHColors.softDangerBorderAlpha26
        )
        this.issuesScrollView.viewHTMLElement.setAttribute("role", "list")
        this.issuesScrollView.viewHTMLElement.setAttribute(
            "aria-label",
            "Parandamist vajavad väljad"
        )

        this._issueLabels = validationIssues.map((validationIssue, issueIndex) => {
            const issueLabel = new UITextView(undefined, UITextView.type.paragraph)
                .configuredWithObject({
                    text: `${issueIndex + 1}. ${validationIssue.message}`,
                    textColor: RHColors.textPrimary,
                    isSingleLine: NO,
                    style: {
                        fontSize: RHFont.sizeMedium,
                        fontWeight: RHFont.weightRegular,
                        lineHeight: RHFont.lineHeightBase
                    }
                })
                .addedAsSubviewToView(this.issuesScrollView)
            issueLabel.viewHTMLElement.setAttribute("role", "listitem")
            return issueLabel
        })
    }


    _issueListContentHeight(constrainingWidth: number): number {
        const labelWidth = Math.max(
            0,
            constrainingWidth - RHValidationIssuesDialogViewTokens.issueListInset * 2
        )
        let contentHeight = RHValidationIssuesDialogViewTokens.issueListInset * 2

        this._issueLabels.forEach((issueLabel, issueIndex) => {
            if (issueIndex > 0) {
                contentHeight = contentHeight + RHValidationIssuesDialogViewTokens.issueRowGap
            }
            contentHeight = contentHeight + issueLabel.intrinsicContentHeight(labelWidth)
        })

        return contentHeight
    }


    override layoutSubviews() {
        super.layoutSubviews()

        const bounds = this.contentBoundsWithInset(0)
        const visibleIssueListHeight = [
            this._issueListContentHeight(bounds.width),
            RHValidationIssuesDialogViewTokens.issueListMaxHeight
        ].min()

        bounds
            .rectangleWithHeight(this.titleLabel)
            .assignedAsFrameOfView(this.titleLabel)
            .rectangleForNextRow(
                RHValidationIssuesDialogViewTokens.titleToGuidanceGap,
                this.guidanceLabel
            )
            .assignedAsFrameOfView(this.guidanceLabel)
            .rectangleForNextRow(
                RHValidationIssuesDialogViewTokens.guidanceToIssueListGap,
                visibleIssueListHeight
            )
            .assignedAsFrameOfView(this.issuesScrollView)

        this.issuesScrollView
            .contentBoundsWithInset(RHValidationIssuesDialogViewTokens.issueListInset)
            .framesByDistributingViewsAsColumn(
                this._issueLabels,
                RHValidationIssuesDialogViewTokens.issueRowGap
            )
    }


    override intrinsicContentHeight(constrainingWidth = 0): number {
        const issueListHeight = [
            this._issueListContentHeight(constrainingWidth),
            RHValidationIssuesDialogViewTokens.issueListMaxHeight
        ].min()

        return this.titleLabel.intrinsicContentHeight(constrainingWidth) +
            RHValidationIssuesDialogViewTokens.titleToGuidanceGap +
            this.guidanceLabel.intrinsicContentHeight(constrainingWidth) +
            RHValidationIssuesDialogViewTokens.guidanceToIssueListGap +
            issueListHeight
    }


    override intrinsicContentWidth(): number {
        return RHValidationIssuesDialogViewTokens.preferredWidth
    }

}
